import * as React from "react";
import { nanoid } from "nanoid";
import { X } from "lucide-react";
import * as Toast from "@radix-ui/react-toast";
import { useTranslation } from "react-i18next";
import {
  getNotificationNavigationTarget,
  getNotificationPresentation,
} from "@/lib/notifications";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { cn } from "@/lib/utils";
import { navigationService } from "@/utils/navigationService";
import type { NotificationEntity } from "@/types/notifications";

type NotifToast = {
  id: string;
  notification: Pick<
    NotificationEntity,
    "type" | "priority" | "metadata" | "reference" | "bookingId"
  >;
  title: string;
  description?: string;
  duration?: number;
};

type Ctx = {
  notify: (t: Omit<NotifToast, "id">) => void;
  notifyMany: (
    items: Array<Omit<NotifToast, "id">>,
    opts?: { max?: number },
  ) => void;
};

const NotificationsToastContext = React.createContext<Ctx | null>(null);

export function useNotifyToast() {
  const ctx = React.useContext(NotificationsToastContext);
  if (!ctx) {
    throw new Error(
      "useNotifyToast must be used within NotificationsToastProvider",
    );
  }
  return ctx;
}

const toastBase =
  "rounded-2xl border border-border bg-background " +
  "p-[16px] w-[336px] flex gap-4 items-start shadow-[0_12px_32px_rgba(0,0,0,0.12)]";

const motion =
  "data-[state=open]:animate-in data-[state=closed]:animate-out " +
  "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 " +
  "data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-right-2";

const titleCls = "text-[14px] font-semibold leading-[20px]";
const descCls = "mt-[2px] text-[14px] leading-[20px] text-muted-foreground";
const closeCls =
  "ml-2 shrink-0 rounded-md p-1 text-muted-foreground transition hover:text-foreground cursor-pointer";

export function NotificationsToastProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const [toasts, setToasts] = React.useState<NotifToast[]>([]);

  const remove = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const notify = React.useCallback((toast: Omit<NotifToast, "id">) => {
    setToasts((prev) =>
      [{ id: nanoid().toString(), ...toast }, ...prev].slice(0, 6),
    );
  }, []);

  const notifyMany = React.useCallback(
    (items: Array<Omit<NotifToast, "id">>, opts?: { max?: number }) => {
      const max = opts?.max ?? 3;
      const sliced = items.slice(0, max);
      const rest = items.length - sliced.length;

      setToasts((prev) => {
        const created = sliced.map((toast) => ({
          id: nanoid().toString(),
          ...toast,
        }));

        const extra: NotifToast[] =
          rest > 0
            ? [
                {
                  id: nanoid().toString(),
                  notification: {
                    type: "SYSTEM",
                    priority: "NORMAL",
                    metadata: null,
                    reference: null,
                    bookingId: null,
                  },
                  title: t("notifications.moreNotificationsTitle", {
                    count: rest,
                  }),
                  description: t("notifications.moreNotificationsDescription"),
                  duration: 5000,
                },
              ]
            : [];

        return [...created, ...extra, ...prev].slice(0, 6);
      });
    },
    [t],
  );

  return (
    <NotificationsToastContext.Provider value={{ notify, notifyMany }}>
      <Toast.Provider swipeDirection="right">
        {children}

        {toasts.map((toast) => {
          const presentation = getNotificationPresentation(toast.notification);
          const navigationTarget = getNotificationNavigationTarget(
            toast.notification,
            userProfile,
          );
          const Icon = presentation.icon;

          return (
            <Toast.Root
              key={toast.id}
              duration={toast.duration ?? 7000}
              className={cn(
                toastBase,
                motion,
                navigationTarget
                  ? "cursor-pointer transition-shadow hover:shadow-[0_16px_40px_rgba(0,0,0,0.16)]"
                  : "",
              )}
              onOpenChange={(open) => {
                if (!open) remove(toast.id);
              }}
              onClick={() => {
                if (!navigationTarget) return;
                navigationService.navigate(navigationTarget);
                remove(toast.id);
              }}
            >
              <Icon
                className={cn(
                  "mt-0.5 size-4 shrink-0",
                  presentation.iconClassName,
                )}
              />

              <div className="flex-1">
                <Toast.Title
                  className={cn(titleCls, presentation.titleClassName)}
                >
                  {toast.title}
                </Toast.Title>

                {toast.description ? (
                  <Toast.Description className={descCls}>
                    {toast.description}
                  </Toast.Description>
                ) : null}
              </div>

              <Toast.Close
                className={closeCls}
                aria-label={t("notifications.close")}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <X className="size-4" />
              </Toast.Close>
            </Toast.Root>
          );
        })}

        <Toast.Viewport
          className="
            fixed top-5 right-5 z-[9999]
            flex flex-col gap-4
            w-[336px] max-w-[calc(100vw-2rem)]
            outline-none
          "
        />
      </Toast.Provider>
    </NotificationsToastContext.Provider>
  );
}
